import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Calendar as CalendarIcon, CheckCircle2, XCircle, RefreshCw,
  Loader2, Clock, Link2, RotateCcw,
} from 'lucide-react';

export default function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [intervalDraft, setIntervalDraft] = useState('5');
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, c, cl] = await Promise.all([
        api.get('/gcal/status'),
        api.get('/gcal/calendars'),
        api.get('/clinics'),
      ]);
      setStatus(s.data);
      setCalendars(c.data);
      setClinics(cl.data);
      setIntervalDraft(String(s.data?.interval_minutes ?? 5));
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post('/gcal/scheduler', { enabled });
      setStatus(data);
      toast.success(enabled ? 'Google Calendar sync enabled' : 'Google Calendar sync paused');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally { setBusy(false); }
  };

  const saveInterval = async () => {
    const n = parseInt(intervalDraft, 10);
    if (!Number.isFinite(n) || n < 2 || n > 1440) {
      toast.error('Interval must be 2-1440 minutes'); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/gcal/scheduler', { interval_minutes: n });
      setStatus(data);
      toast.success(`Interval set to ${data.interval_minutes} min`);
    } catch { toast.error('Failed to update interval'); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/gcal/scheduler/run-now');
      setStatus(data);
      const ss = data?.service?.last_run?.stats;
      if (ss) {
        toast.success(`Synced ${ss.calendars_synced}/${ss.calendars_seen} calendars · ${ss.events_upserted} events`);
      } else {
        toast.info('Sync triggered');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setRunning(false); }
  };

  const resetTokens = async () => {
    if (!confirm('Reset all syncTokens? Next sync will re-do a FULL backfill for every calendar.')) return;
    setBusy(true);
    try {
      await api.post('/gcal/reset-tokens');
      toast.success('Sync tokens cleared — next run will backfill everything');
      await load();
    } catch { toast.error('Failed to reset'); }
    finally { setBusy(false); }
  };

  const updateCal = async (calId, patch) => {
    try {
      await api.patch(`/gcal/calendars/${encodeURIComponent(calId)}`, patch);
      await load();
    } catch { toast.error('Failed to update calendar mapping'); }
  };

  const svc = status?.service || {};
  const configured = svc.configured;
  const lastRun = svc.last_run;

  return (
    <Card
      className="p-4 border-sky-200 bg-gradient-to-br from-sky-50/40 to-white"
      data-testid="gcal-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-sky-100 flex items-center justify-center">
          <CalendarIcon className="w-4 h-4 text-sky-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-stone-900">Google Calendar (Clinic Backfill)</div>
            {!configured ? (
              <Badge variant="outline" className="text-[9px]" data-testid="gcal-status-badge">
                <XCircle className="w-2.5 h-2.5 mr-0.5" />Not configured
              </Badge>
            ) : status?.enabled ? (
              <Badge className="bg-emerald-100 text-emerald-800 text-[9px]" data-testid="gcal-status-badge">
                <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Live · every {status.interval_minutes} min
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[9px]" data-testid="gcal-status-badge">
                <XCircle className="w-2.5 h-2.5 mr-0.5" />Paused
              </Badge>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            {configured ? (
              <>Impersonating <code className="text-[10px] px-1 bg-stone-100 rounded font-mono">{svc.impersonate_email}</code> · pulls all events (past + future) into HearClear OS. Backfill on first pass, then delta via syncToken.</>
            ) : (
              <>Set <code className="text-[10px] px-1 bg-stone-100 rounded font-mono">GCAL_SA_JSON</code> + <code className="text-[10px] px-1 bg-stone-100 rounded font-mono">GCAL_IMPERSONATE_EMAIL</code> in backend/.env to enable Google Calendar sync via Service Account + Domain-Wide Delegation.</>
            )}
          </div>

          {configured && (
            <>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Switch
                    checked={!!status?.enabled}
                    onCheckedChange={toggle}
                    disabled={busy}
                    data-testid="gcal-toggle"
                  />
                  <span className="text-xs text-stone-700 font-medium">
                    {status?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-stone-500" />
                  <Input
                    type="number"
                    min={2}
                    max={1440}
                    value={intervalDraft}
                    onChange={(e) => setIntervalDraft(e.target.value)}
                    className="h-7 w-20 text-xs"
                    data-testid="gcal-interval-input"
                  />
                  <span className="text-xs text-stone-500">min</span>
                  <Button size="sm" variant="outline" onClick={saveInterval} disabled={busy}
                          className="h-7 text-xs" data-testid="gcal-save-interval">Save</Button>
                </div>
                <Button
                  size="sm"
                  onClick={runNow}
                  disabled={running || !status?.enabled}
                  className="h-8 bg-sky-700 hover:bg-sky-800 text-white"
                  data-testid="gcal-run-now"
                >
                  {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                  Sync Now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetTokens}
                  disabled={busy}
                  className="h-8 text-xs"
                  data-testid="gcal-reset-tokens"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Full re-backfill
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3 max-w-xl">
                <Stat label="Calendars" value={svc.total_calendars} />
                <Stat label="Selected" value={svc.selected_calendars} />
                <Stat label="Events (live)" value={svc.total_events} />
              </div>

              {status?.next_run_at && (
                <div className="text-[11px] text-stone-500 mt-2 font-mono">
                  Next run: {new Date(status.next_run_at).toLocaleString()}
                </div>
              )}
              {lastRun && (
                <div className="text-[11px] text-stone-600 mt-1 font-mono">
                  Last: {new Date(lastRun.started_at).toLocaleString()} — {lastRun.status}
                  {lastRun.stats && (
                    <> · {lastRun.stats.calendars_synced}/{lastRun.stats.calendars_seen} cals · +{lastRun.stats.events_upserted} events</>
                  )}
                </div>
              )}
              {status?.recent_errors?.length > 0 && (
                <div className="text-[11px] text-red-700 mt-1">
                  Recent error: {status.recent_errors[status.recent_errors.length - 1].err?.slice(0, 140)}
                </div>
              )}

              {calendars.length > 0 && (
                <div className="mt-4 border-t border-stone-100 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">
                    Calendar → Clinic Mapping ({calendars.length})
                  </div>
                  <div className="max-h-72 overflow-auto space-y-1">
                    {calendars.map((c) => (
                      <div key={c.gcal_calendar_id} className="grid grid-cols-[1fr_180px_60px] gap-2 items-center py-1 border-b border-stone-50 last:border-0 text-xs">
                        <div className="min-w-0">
                          <div className="font-medium text-stone-900 truncate flex items-center gap-1.5">
                            {c.primary && <Link2 className="w-3 h-3 text-sky-600" />}
                            {c.summary}
                          </div>
                          <div className="text-[10px] text-stone-400 truncate">{c.gcal_calendar_id}</div>
                        </div>
                        <select
                          value={c.clinic_id || ''}
                          onChange={(e) => updateCal(c.gcal_calendar_id, { clinic_id: e.target.value || null })}
                          className="text-xs px-1.5 py-1 rounded border border-stone-200 bg-white"
                          data-testid={`gcal-clinic-${c.gcal_calendar_id.slice(0, 12)}`}
                        >
                          <option value="">— unmapped —</option>
                          {clinics.map((cl) => (
                            <option key={cl.id} value={cl.id}>{cl.name}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 text-[10px] text-stone-600">
                          <input
                            type="checkbox"
                            checked={c.selected}
                            onChange={(e) => updateCal(c.gcal_calendar_id, { selected: e.target.checked })}
                          /> Sync
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-stone-200 px-3 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-lg font-heading font-bold text-stone-900 tabular-nums">{value ?? '—'}</div>
    </div>
  );
}
